import React from "react";
import { Box, Flex, useMediaQuery } from "@chakra-ui/react";
import { colors } from "../../definitions/constants/GlobalStyles";
import { PostData } from "../../definitions/interfaces/GlobalInterfaces";
import MediaComponent from "./MediaComponent";
import AuthorInfoComponent from "./AuthorInfoComponent";
import TextComponent from "./TextComponent";
import SendCommentsComponent from "./SendCommentsComponent";
import ShareCountComponent from "./ShareCountComponent";
import CommentsListComponent from "./CommentsListComponent";
import * as IconsComponent from "../../definitions/constants/IconsComponent";
import i18n from "../../i18n/i18n";
import { useNavigate } from "react-router-dom";
import { appStore } from "../../zustand/globalStore";

interface PostItemComponentProps {
    post: PostData;
}

export default function PostItemComponent({ post }: PostItemComponentProps) {
    const [isMobile] = useMediaQuery("(max-width: 992px)");
    const navigate = useNavigate();
    const { userData } = appStore((state) => state);

    const handleAuthCheck = (e: React.MouseEvent) => {
        if (!userData) {
            e.preventDefault();
            e.stopPropagation();
            navigate("/login");
        }
    };

    let parsedStructureText = post.structure_text;
    if (typeof parsedStructureText === "string") {
        try {
            parsedStructureText = JSON.parse(parsedStructureText);
        } catch (e) {
            console.error("Error parsing structure_text:", e);
        }
    }

    return (
        <Box
            key={post.id}
            bg={"#FFFFFF"}
            borderBottom={`1px solid ${colors.figmaColors.grey1}`}
            p={{ base: "8px 12px", md: "12px 16px" }}
            borderRadius={{ base: "none", md: "16px" }}
            boxShadow="sm"
        >
            <Flex gap={4} wrap={!isMobile ? "nowrap" : "wrap"}>
                {!isMobile ? (
                    <Box flex="55%">
                        <MediaComponent post={post} />
                    </Box>
                ) : null}

                <Box
                    flex={!isMobile ? "45%" : "100%"}
                    position="relative"
                    minHeight="400px"
                    display="flex"
                    flexDirection="column"
                >
                    {post.post_type === "shared_post" && parsedStructureText?.shared_from && (
                        <Flex align="center" gap={2} mb={2}>
                            <IconsComponent.ShareIcon stroke={colors.figmaColors.secColor} />
                            <span style={{ fontSize: "12px", color: colors.figmaColors.grey3, fontStyle: "italic" }}>
                                {i18n.t("Shared from")} <b
                                    style={{ cursor: "pointer", color: colors.figmaColors.secColor }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        navigate(`/profile/${parsedStructureText.shared_from.user_id}`);
                                    }}
                                >
                                    {parsedStructureText.shared_from.firstname} {parsedStructureText.shared_from.lastname}
                                </b>
                            </span>
                        </Flex>
                    )}

                    <Box marginBottom={2}>
                        <AuthorInfoComponent post={post} />
                    </Box>

                    <Box marginBottom={2}>
                        <TextComponent post={post} />
                    </Box>

                    {post.post_type === "shared_post" && parsedStructureText?.shared_from && (
                        <Box
                            mb={4}
                            border={`1px solid ${colors.figmaColors.grey1}`}
                            borderRadius="12px"
                            p={3}
                            bg={colors.figmaColors.grey0}
                        >
                            <Flex align="center" gap={3} mb={2}>
                                {parsedStructureText.shared_from.image_url ? (
                                    <img
                                        src={parsedStructureText.shared_from.image_url}
                                        alt="Original Author"
                                        style={{ width: "32px", height: "32px", borderRadius: "50%", objectFit: "cover", cursor: "pointer" }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            navigate(`/profile/${parsedStructureText.shared_from.user_id}`);
                                        }}
                                    />
                                ) : (
                                    <Box
                                        w="32px" h="32px" borderRadius="50%" bg={colors.figmaColors.grey1} cursor="pointer"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            navigate(`/profile/${parsedStructureText.shared_from.user_id}`);
                                        }}
                                    />
                                )}
                                <Box>
                                    <span
                                        style={{ fontWeight: 600, fontSize: "14px", color: colors.figmaColors.black, display: "block", cursor: "pointer" }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            navigate(`/profile/${parsedStructureText.shared_from.user_id}`);
                                        }}
                                        onMouseEnter={(e) => (e.currentTarget.style.textDecoration = 'underline')}
                                        onMouseLeave={(e) => (e.currentTarget.style.textDecoration = 'none')}
                                    >
                                        {parsedStructureText.shared_from.firstname} {parsedStructureText.shared_from.lastname}
                                    </span>
                                    <span style={{ fontSize: "12px", color: colors.figmaColors.grey3 }}>
                                        @{parsedStructureText.shared_from.username}
                                    </span>
                                </Box>
                            </Flex>
                            {parsedStructureText.shared_from.original_text && (
                                <span style={{ fontSize: "14px", color: colors.figmaColors.black, display: "block", marginBottom: isMobile ? "12px" : "0" }}>
                                    {parsedStructureText.shared_from.original_text}
                                </span>
                            )}
                            {isMobile && (
                                <Box mt={2}>
                                    <MediaComponent post={post} />
                                </Box>
                            )}
                        </Box>
                    )}

                    {isMobile && post.post_type !== "shared_post" ? (
                        <Box my={4}>
                            <MediaComponent post={post} />
                        </Box>
                    ) : null}

                    {post.generated_by_ia && (
                        <Box mt="auto" mb={2}>
                            <span style={{ fontSize: "12px", color: "#123EC9", fontStyle: "italic", fontWeight: 500 }}>
                                ✨ {i18n.t("generatedByIA")}
                            </span>
                        </Box>
                    )}

                    {post.num_comments > 0 && (
                        <Box>
                            <CommentsListComponent post={post} />
                        </Box>
                    )}

                    <Box
                        position={{ base: "relative", lg: "absolute" }}
                        bottom="0"
                        left="0"
                        width="100%"
                        bg={"#FFFFFF"}
                        pt={4}
                    >
                        <Box marginBottom={4} onClick={handleAuthCheck}>
                            <ShareCountComponent post={post} />
                        </Box>

                        <Box onClick={handleAuthCheck}>
                            <SendCommentsComponent post={post} />
                        </Box>
                    </Box>
                </Box>
            </Flex>
        </Box>
    );
}
